import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddedFile } from "@/features/chat-management/components/AddedFile";
import UploadItem from "@/shared/models/UploadItem";
import { AddedWebpage } from "@/features/chat-management/components/AddedWebpage";
import { checkUploadedItems } from "@/shared/utils/UploadedItems";

interface UploadOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: UploadItem[];
  onItemAdd: (item: UploadItem) => void;
  onItemRemove: (itemId: number) => void;
  onItemStatusUpdate: (item: UploadItem) => void;
}

export function UploadOverlay({
  isOpen,
  onClose,
  selectedItems,
  onItemAdd,
  onItemRemove,
  onItemStatusUpdate,
}: UploadOverlayProps) {
  const [url, setUrl] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const div = dragRef.current;
    if (!div) return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes("Files")) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = div.getBoundingClientRect();
      if (
        e.clientX <= rect.left ||
        e.clientX >= rect.right ||
        e.clientY <= rect.top ||
        e.clientY >= rect.bottom
      ) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes("Files")) {
        setIsDragging(true);
      } else {
        setIsDragging(false);
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;

      files.forEach((file) => {
        const size =
          file.size < 1024 * 1024
            ? `${(file.size / 1024).toFixed(1)} KB`
            : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;

        const itemId = Date.now() + Math.random();
        const newItem: UploadItem = {
          id: itemId,
          name: file.name,
          type: "file" as const,
          size,
          status: "toProcess" as const,
          file: file,
        };

        onItemAdd(newItem);
      });
    };

    div.addEventListener("dragenter", handleDragEnter);
    div.addEventListener("dragleave", handleDragLeave);
    div.addEventListener("dragover", handleDragOver);
    div.addEventListener("drop", handleDrop);

    return () => {
      div.removeEventListener("dragenter", handleDragEnter);
      div.removeEventListener("dragleave", handleDragLeave);
      div.removeEventListener("dragover", handleDragOver);
      div.removeEventListener("drop", handleDrop);
    };
  }, [onItemAdd]);

  useEffect(() => {
    const handleWindowDragLeave = (e: DragEvent) => {
      if (!e.clientX && !e.clientY) {
        setIsDragging(false);
      }
    };

    window.addEventListener("dragleave", handleWindowDragLeave);
    return () => {
      window.removeEventListener("dragleave", handleWindowDragLeave);
    };
  }, []);

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleUrlSubmit = () => {
    if (!url.trim()) return;

    const isValid = validateUrl(url);
    setIsValidUrl(isValid);

    if (isValid) {
      const newItem: UploadItem = {
        id: Date.now(),
        name: url,
        type: "url" as const,
        url: url,
        status: "toProcess" as const,
      };
      onItemAdd(newItem);
      setUrl("");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      // Convert file size to readable format
      const size =
        file.size < 1024 * 1024
          ? `${(file.size / 1024).toFixed(1)} KB`
          : `${(file.size / (1024 * 1024)).toFixed(1)} MB`;

      const itemId = Date.now() + Math.random();
      const newItem: UploadItem = {
        id: itemId,
        name: file.name,
        type: "file" as const,
        size,
        status: "toProcess" as const,
        file: file, // Store the actual File object
      };

      onItemAdd(newItem);
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDoneClick = () => {
    if (!checkUploadedItems(selectedItems)) return;

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[800px] h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              Add File or URL
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto min-h-0">
          {/* File Upload Section */}
          <div className="mb-8">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              Upload Files
            </h4>
            <div
              ref={dragRef}
              className={cn(
                "border-2 border-dashed rounded-lg p-12 text-center cursor-pointer min-h-[350px] flex flex-col items-center justify-center",
                isDragging
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-200",
                !isDragging && "hover:border-gray-300",
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                ref={fileInputRef}
                multiple
                id="file-upload"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.json,.xml,.md,.txt"
              />
              <div className="cursor-pointer flex flex-col items-center">
                <Upload className="h-10 w-10 text-gray-400 mb-4" />
                <span className="text-sm text-gray-600">
                  Click to select files or drag and drop
                </span>
              </div>
            </div>

            {/* Selected Files List */}
            {selectedItems.filter((item) => item.type === "file").length >
              0 && (
              <div className="mt-4 space-y-2">
                {selectedItems
                  .filter((item) => item.type === "file")
                  .map((file) => (
                    <AddedFile
                      key={file.id}
                      file={file}
                      onItemRemove={onItemRemove}
                      onItemStatusUpdate={onItemStatusUpdate}
                    />
                  ))}
              </div>
            )}
          </div>

          {/* URL Input Section */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Add URLs</h4>
            <div className="gap-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Paste URL here..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className={cn(
                    "flex-1",
                    !isValidUrl && "border-red-500 focus-visible:ring-red-500",
                  )}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleUrlSubmit();
                    }
                  }}
                />
                <Button onClick={handleUrlSubmit} variant="outline">
                  Add
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Quantly will parse the web page provided. Please note it will
                not follow the links on the web page.
              </p>
              {/* URL List */}
              {selectedItems.filter((item) => item.type === "url").length >
                0 && (
                <div className="space-y-2">
                  {selectedItems
                    .filter((item) => item.type === "url")
                    .map((item) => (
                      <AddedWebpage
                        key={item.id}
                        item={item}
                        onItemRemove={onItemRemove}
                        onItemStatusUpdate={onItemStatusUpdate}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedItems.length} items added
          </div>
          <Button onClick={handleDoneClick}>Done</Button>
        </div>
      </div>
    </div>
  );
}
